import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import {
  ArrowLeft, Mail, Phone, MapPin, Briefcase, FileText, Wallet, Home,
  TrendingUp, MessageSquare, Lock, Save, CheckCircle2, Clock,
  ExternalLink, Plus, ShieldCheck, Activity, Calendar, User as UserIcon,
  AlertTriangle, FileSignature, Send, Copy, ChevronRight, Handshake,
  Sun, Wrench, Package,
} from "lucide-react";
import DocumentHub from "@/components/DocumentHub";
import PropertiesTab from "@/pages/admin/PropertiesTab";
import NewInvoiceModal from "@/components/NewInvoiceModal";
import KpiCard, { KpiGrid } from "@/components/KpiCard";
import DataTable from "@/components/DataTable";
import EmptyState from "@/components/EmptyState";
import RowActions from "@/components/RowActions";
import StatusPill from "@/components/StatusPill";
import { SkeletonCard } from "@/components/Skeleton";
import { formatMoney, formatDate, formatRelative } from "@/lib/format";

const TAB_META = [
  { key: "overview",    label: "Overview",    icon: Activity },
  { key: "projects",    label: "Projects",    icon: Briefcase },
  { key: "properties",  label: "Properties",  icon: Home },
  { key: "work_orders", label: "Work orders", icon: Wrench },
  { key: "invoices",    label: "Invoices",    icon: FileText },
  { key: "payments",    label: "Payments",    icon: Wallet },
  { key: "requests",    label: "Requests",    icon: MessageSquare },
  { key: "documents",   label: "Documents",   icon: FileSignature },
];

export default function AdminClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [invoicing, setInvoicing] = useState(false);

  const load = () =>
    api.get(`/admin/clients/${id}/360`).then((r) => {
      setData(r.data);
      setNotes(r.data.user.internal_notes || "");
    }).catch(() => toast.error("Could not load client"));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/users/${id}/internal-notes`, { notes });
      toast.success("Notes saved");
    } catch { toast.error("Save failed"); }
    finally { setSavingNotes(false); }
  };

  const approve = async () => {
    if (!window.confirm("Grant full access?")) return;
    await api.post(`/users/${id}/approve`);
    toast.success("Approved");
    load();
  };
  const revoke = async () => {
    if (!window.confirm("Revoke access?")) return;
    await api.post(`/users/${id}/revoke`);
    toast.success("Access revoked");
    load();
  };

  if (!data) {
    return (
      <div className="max-w-7xl mx-auto space-y-4" data-testid="admin-client-detail-loading">
        <SkeletonCard />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3"><SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard /></div>
        <SkeletonCard />
      </div>
    );
  }

  const { user, stats, projects, work_orders, invoices, payments, requests } = data;
  const tabCount = {
    projects: projects.length,
    properties: null,
    work_orders: work_orders.length,
    invoices: invoices.length,
    payments: payments.length,
    requests: requests.length,
    documents: null,
  };

  return (
    <div className="max-w-7xl mx-auto space-y-5" data-testid="admin-client-detail">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-[#94A3B8]" data-testid="crumb">
        <Link to="/admin/clients" className="hover:text-[#0A2540] dark:hover:text-white inline-flex items-center gap-1" data-testid="back-clients">
          <ArrowLeft className="w-3 h-3" /> Clients
        </Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-[#0A2540] dark:text-white font-medium truncate">{user.name}</span>
      </div>

      <ClientHeader
        user={user}
        onApprove={approve}
        onRevoke={revoke}
        onNewInvoice={() => setInvoicing(true)}
        onNewProject={() => navigate(`/admin/projects?client=${user.user_id}`)}
      />

      {/* KPI strip */}
      <KpiGrid className="pb-reveal-child">
        <KpiCard
          icon={TrendingUp} tone="neutral" testId="kpi-lifetime"
          label="Lifetime value"
          value={stats.lifetime_revenue || stats.paid_total || 0}
          money decimals={0}
          sub={`${stats.invoices_count} invoice${stats.invoices_count === 1 ? "" : "s"} on record`}
        />
        <KpiCard
          icon={Wallet}
          tone={stats.overdue_total > 0 ? "critical" : stats.outstanding_total > 0 ? "attention" : "positive"}
          testId="kpi-outstanding"
          label="Outstanding"
          value={stats.outstanding_total}
          money decimals={0}
          sub={stats.overdue_total > 0
            ? `${formatMoney(stats.overdue_total, { decimals: 0 })} overdue`
            : "In good standing"}
        />
        <KpiCard
          icon={Briefcase} tone="neutral" testId="kpi-projects"
          label="Projects"
          value={stats.projects_total}
          sub={`${stats.projects_active} active · ${stats.projects_completed} done`}
        />
        <KpiCard
          icon={Handshake}
          tone={user.access_level === "approved" ? "positive" : "attention"}
          testId="kpi-tenure"
          label="Client since"
          value={monthsSince(user.created_at)}
          suffix={monthsSince(user.created_at) === 1 ? " mo" : " mos"}
          sub={user.approved_at
            ? `Approved ${formatDate(user.approved_at)}`
            : "Awaiting approval"}
        />
      </KpiGrid>

      {/* Overdue banner */}
      {stats.overdue_total > 0 && (
        <div className="rounded-xl border border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-900/10 p-4 flex items-start gap-3" data-testid="overdue-banner">
          <div className="w-9 h-9 rounded-lg bg-red-500 grid place-items-center text-white shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[#0A2540] dark:text-white text-sm">
              {formatMoney(stats.overdue_total, { decimals: 0 })} overdue on this account
            </div>
            <div className="text-xs text-[#475569] dark:text-slate-400 mt-0.5">
              Review the invoices tab and send a reminder or update the terms before the next work order.
            </div>
          </div>
          <button
            onClick={() => setTab("invoices")}
            data-testid="banner-view-invoices"
            className="h-9 px-3 rounded-md text-xs font-semibold bg-red-600 hover:bg-red-700 text-white whitespace-nowrap"
          >
            Review invoices
          </button>
        </div>
      )}

      {/* Main content + sticky sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-5">
        {/* Left column — tabs */}
        <div className="card-pb overflow-hidden">
          <div className="border-b border-[#E2E8F0] dark:border-slate-700 overflow-x-auto no-scrollbar">
            <div className="flex gap-0 min-w-max">
              {TAB_META.map(({ key, label, icon: Icon }) => {
              const active = tab === key;
              const count = tabCount[key];
              return (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  data-testid={`tab-${key}`}
                  className={`inline-flex items-center gap-2 px-4 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
                    active
                      ? "border-[#C9A356] text-[#0A2540] dark:text-white"
                      : "border-transparent text-[#475569] dark:text-slate-400 hover:text-[#0A2540] dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800/30"
                  }`}
                >
                  <Icon className={`w-4 h-4 ${active ? "text-[#C9A356]" : ""}`} />
                  {label}
                  {typeof count === "number" && count > 0 && (
                    <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 tabular-nums ${
                      active
                        ? "bg-[#C9A356]/15 text-[#8A6B14] dark:text-[#DDBD77]"
                        : "bg-slate-100 dark:bg-slate-800 text-[#475569] dark:text-slate-400"
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
            </div>
          </div>

          <div className="p-5 sm:p-6">
            {tab === "overview" && <OverviewTab data={data} onJump={setTab} />}
            {tab === "projects" && <ProjectsTab projects={projects} />}
            {tab === "properties" && <PropertiesTab userId={id} />}
            {tab === "work_orders" && <WorkOrdersTab items={work_orders} />}
            {tab === "invoices" && <InvoicesTab invoices={invoices} />}
            {tab === "payments" && <PaymentsTab payments={payments} />}
            {tab === "requests" && <RequestsTab requests={requests} />}
            {tab === "documents" && <DocumentHub userId={id} />}
          </div>
        </div>

        {/* Right sticky sidebar */}
        <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start" data-testid="client-sidebar">
          <KeyDetailsCard user={user} stats={stats} />
          <InternalNotesCard
            notes={notes}
            setNotes={setNotes}
            saving={savingNotes}
            onSave={saveNotes}
          />
        </aside>
      </div>

      {invoicing && (
        <NewInvoiceModal
          open={invoicing}
          prefillClientId={user.user_id}
          onClose={() => setInvoicing(false)}
          onCreated={() => { setInvoicing(false); load(); }}
        />
      )}
    </div>
  );
}

/* ==================== Header ==================== */

function ClientHeader({ user, onApprove, onRevoke, onNewInvoice, onNewProject }) {
  const approved = user.access_level === "approved";
  const initials = (user.name || user.email || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const copyPB = () => {
    const code = user.customer_number || user.customer_code;
    if (!code) return;
    navigator.clipboard.writeText(code);
    toast.success(`${code} copied`);
  };

  return (
    <div className="card-pb p-5 sm:p-7 relative overflow-hidden" data-testid="client-header">
      {/* Subtle top-right brand accent */}
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#C9A356]/8 pointer-events-none" />
      <div className="flex flex-wrap items-start justify-between gap-5 relative">
        <div className="flex items-start gap-4 min-w-0">
          {/* Avatar */}
          {user.picture ? (
            <img src={user.picture} alt="" className="w-16 h-16 rounded-2xl object-cover ring-2 ring-white dark:ring-slate-800 shadow" />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0A2540] to-[#12365b] text-white grid place-items-center text-xl font-heading font-semibold ring-2 ring-white dark:ring-slate-800 shadow">
              {initials}
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={copyPB}
                data-testid="copy-customer-code"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] font-bold text-[#C9A356] hover:text-[#DDBD77] font-mono"
                title="Copy customer number"
              >
                {user.customer_number || user.customer_code}
                <Copy className="w-3 h-3 opacity-60" />
              </button>
              <StatusPill tone={approved ? "success" : "warning"} dot testId={approved ? "status-approved" : "status-pending"}>
                {approved ? "Approved" : "Pending"}
              </StatusPill>
            </div>
            <h1 className="font-heading text-3xl text-[#0A2540] dark:text-white mt-1 truncate">
              {user.name || "Unnamed client"}
            </h1>
            <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-[#475569] dark:text-slate-400">
              <a href={`mailto:${user.email}`} data-testid="contact-email"
                 className="inline-flex items-center gap-1.5 hover:text-[#C9A356] transition-colors">
                <Mail className="w-3.5 h-3.5" /> {user.email}
              </a>
              {user.phone && (
                <a href={`tel:${user.phone}`} data-testid="contact-phone"
                   className="inline-flex items-center gap-1.5 hover:text-[#C9A356] transition-colors">
                  <Phone className="w-3.5 h-3.5" /> {user.phone}
                </a>
              )}
              {user.address && (
                <span className="inline-flex items-center gap-1.5 truncate max-w-xs">
                  <MapPin className="w-3.5 h-3.5" /> {user.address}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {!approved && (
            <button onClick={onApprove} data-testid="approve-btn"
                    className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow-sm">
              <CheckCircle2 className="w-4 h-4" /> Approve
            </button>
          )}
          <button
            onClick={onNewInvoice}
            disabled={!approved}
            title={approved ? "Create a draft invoice" : "Approve client before invoicing"}
            data-testid="new-invoice-btn"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-[#C9A356] hover:bg-[#DDBD77] text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" /> Invoice
          </button>
          <button onClick={onNewProject} data-testid="new-project-btn"
                  className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-[#0A2540] hover:bg-[#12365b] text-white text-sm font-semibold">
            <Plus className="w-4 h-4" /> Project
          </button>
          <RowActions
            testId="header-more"
            items={[
              { label: "Send message",   icon: MessageSquare, onClick: () => toast.info("Open the project message thread from a project detail page.") },
              { label: "Copy client link", icon: Copy, onClick: () => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); } },
              ...(approved
                ? [{ label: "Revoke access", icon: Lock, danger: true, onClick: onRevoke }]
                : []),
            ]}
          />
        </div>
      </div>
    </div>
  );
}

/* ==================== Overview tab ==================== */

function OverviewTab({ data, onJump }) {
  const { projects, invoices, payments, requests, work_orders } = data;
  const feed = useMemo(() => buildActivityFeed({ projects, invoices, payments, requests, work_orders }), [projects, invoices, payments, requests, work_orders]);
  const active = projects.filter((p) => !["completed", "cancelled"].includes(p.status));
  const nextInvoice = invoices.find((i) => i.status !== "paid" && i.due_date);

  return (
    <div className="space-y-6 pb-reveal-child">
      {/* Row 1 — At-a-glance mini stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MiniCard title="Active projects" icon={Briefcase} tone="brand">
          {active.length === 0 ? (
            <p className="text-xs text-[#94A3B8] italic">Nothing in-flight.</p>
          ) : (
            <ul className="space-y-2">
              {active.slice(0, 3).map((p) => (
                <li key={p.project_id} className="flex items-center justify-between gap-2 min-w-0">
                  <Link to={`/admin/projects/${p.project_id}`}
                        className="text-sm text-[#0A2540] dark:text-white hover:text-[#C9A356] truncate flex-1"
                        data-testid={`ov-proj-${p.project_id}`}>
                    {p.title}
                  </Link>
                  <ProjectPill status={p.status} />
                </li>
              ))}
            </ul>
          )}
        </MiniCard>
        <MiniCard title="Next payment" icon={Wallet} tone="brand">
          {!nextInvoice ? (
            <p className="text-xs text-[#94A3B8] italic">No open invoices.</p>
          ) : (
            <Link to={`/admin/invoices/${nextInvoice.invoice_id}`} className="block group">
              <div className={`font-heading text-2xl ${nextInvoice.is_overdue ? "text-red-600" : "text-[#0A2540] dark:text-white"} tabular-nums`}>
                {formatMoney(nextInvoice.grand_total || nextInvoice.amount)}
              </div>
              <div className="text-xs text-[#475569] dark:text-slate-400 mt-0.5">
                {nextInvoice.is_overdue ? "Overdue since " : "Due "}
                <b className={nextInvoice.is_overdue ? "text-red-600" : ""}>
                  {formatDate(nextInvoice.due_date)}
                </b>
                <span className="opacity-60"> · {nextInvoice.invoice_number || nextInvoice.invoice_id}</span>
              </div>
            </Link>
          )}
        </MiniCard>
        <MiniCard title="Open requests" icon={MessageSquare} tone="brand">
          {requests.filter((r) => !["closed", "resolved", "converted"].includes(r.status)).length === 0 ? (
            <p className="text-xs text-[#94A3B8] italic">Zero open service requests.</p>
          ) : (
            <ul className="space-y-1">
              {requests.filter((r) => !["closed", "resolved", "converted"].includes(r.status)).slice(0, 3).map((r) => (
                <li key={r.request_id} className="text-sm text-[#0A2540] dark:text-white flex items-center justify-between gap-2">
                  <span className="capitalize truncate">{(r.request_type || "").replace(/_/g, " ")}</span>
                  <StatusPill tone="info" size="xs">{r.status}</StatusPill>
                </li>
              ))}
            </ul>
          )}
        </MiniCard>
      </div>

      {/* Activity timeline */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-base text-[#0A2540] dark:text-white inline-flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#C9A356]" /> Activity timeline
          </h3>
          <span className="text-xs text-[#94A3B8]">{feed.length} events</span>
        </div>

        {feed.length === 0 ? (
          <EmptyState
            icon={Activity}
            title="No activity yet"
            body="Interactions and financial events will appear here as they happen."
          />
        ) : (
          <ol className="relative pl-6 border-l-2 border-[#E2E8F0] dark:border-slate-700 space-y-4">
            {feed.slice(0, 12).map((e, idx) => (
              <li key={`${e.type}-${e.id}-${idx}`} className="relative" data-testid={`activity-${e.id}`}>
                <span className={`absolute -left-[29px] w-4 h-4 rounded-full ring-4 ring-white dark:ring-slate-900 ${e.dot || "bg-slate-400"}`} />
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-[#0A2540] dark:text-white">{e.title}</div>
                    {e.detail && <div className="text-xs text-[#475569] dark:text-slate-400 mt-0.5">{e.detail}</div>}
                  </div>
                  <span className="text-[10px] text-[#94A3B8] shrink-0 whitespace-nowrap">
                    {formatRelative(e.at)}
                  </span>
                </div>
                {e.link && (
                  <Link to={e.link} className="text-[11px] text-[#C9A356] hover:text-[#DDBD77] inline-flex items-center gap-0.5 mt-1">
                    Open <ExternalLink className="w-3 h-3" />
                  </Link>
                )}
              </li>
            ))}
          </ol>
        )}
        {feed.length > 12 && (
          <div className="pt-3 text-center">
            <button onClick={() => onJump("invoices")} className="text-xs text-[#C9A356] hover:text-[#DDBD77]" data-testid="see-more-activity">
              See financial history →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function MiniCard({ title, icon: Icon, tone = "brand", children }) {
  return (
    <div className="rounded-xl border border-[#E2E8F0] dark:border-slate-700 p-4 bg-white dark:bg-slate-900/40 min-h-[112px] transition-all hover:border-[#C9A356]">
      <div className="inline-flex items-center gap-1.5 mb-2">
        <Icon className={`w-3.5 h-3.5 ${tone === "brand" ? "text-[#C9A356]" : "text-emerald-600"}`} />
        <span className="text-[10px] uppercase tracking-wider font-bold text-[#475569] dark:text-slate-400">{title}</span>
      </div>
      {children}
    </div>
  );
}

/* ==================== Tables ==================== */

function ProjectsTab({ projects }) {
  return (
    <DataTable
      testId="table-projects"
      columns={[
        { key: "title", header: "Project", render: (p) => (
          <div className="min-w-0">
            <Link to={`/admin/projects/${p.project_id}`} className="text-[#0A2540] dark:text-white hover:text-[#C9A356] font-medium">
              {p.title}
            </Link>
            <div className="text-[11px] text-[#94A3B8] font-mono">{p.project_number || p.project_id}</div>
          </div>
        )},
        { key: "type",   header: "Type",   render: (p) => (
          <span className="inline-flex items-center gap-1.5 text-xs text-[#475569] dark:text-slate-400 capitalize">
            <ProjectTypeIcon t={p.project_type} /> {(p.project_type || "—").replace(/_/g, " ")}
          </span>
        )},
        { key: "kw",     header: "kW",     align: "right", render: (p) => p.kw_target ? `${p.kw_target.toFixed(1)}` : "—" },
        { key: "amount", header: "Amount", align: "right", render: (p) => formatMoney(p.amount || 0, { decimals: 0 }) },
        { key: "status", header: "Status", render: (p) => <ProjectPill status={p.status} /> },
        { key: "created",header: "Created",render: (p) => <span className="text-[11px] text-[#94A3B8]">{formatRelative(p.created_at)}</span> },
      ]}
      rows={projects}
      getRowId={(p) => p.project_id}
      onRowClick={(p) => { window.location.href = `/admin/projects/${p.project_id}`; }}
      empty={<EmptyState icon={Briefcase} title="No projects yet" body="Once you kick off the first project it will land here." />}
    />
  );
}

function WorkOrdersTab({ items }) {
  return (
    <DataTable
      testId="table-work-orders"
      columns={[
        { key: "title",  header: "Work order", render: (w) => (
          <div className="min-w-0">
            <div className="font-medium text-[#0A2540] dark:text-white truncate">{w.title || w.description || "—"}</div>
            <div className="text-[11px] text-[#94A3B8] font-mono">{w.work_order_id}</div>
          </div>
        )},
        { key: "status", header: "Status",     render: (w) => <StatusPill tone={workOrderTone(w.status)} dot>{(w.status || "").replace(/_/g, " ")}</StatusPill> },
        { key: "date",   header: "Scheduled",  render: (w) => <span className="text-[11px] text-[#94A3B8]">{formatDate(w.scheduled_at || w.created_at)}</span> },
      ]}
      rows={items}
      getRowId={(w) => w.work_order_id}
      empty={<EmptyState icon={Wrench} title="No work orders" body="Field jobs raised for this client will show here." />}
    />
  );
}

function InvoicesTab({ invoices }) {
  const navigate = useNavigate();
  return (
    <DataTable
      testId="table-invoices"
      columns={[
        { key: "id", header: "Invoice", render: (i) => (
          <div className="min-w-0">
            <span className="font-mono text-xs text-[#0A2540] dark:text-white font-semibold">{i.invoice_number || i.invoice_id}</span>
            <div className="text-xs text-[#475569] dark:text-slate-400 truncate">{i.title}</div>
          </div>
        )},
        { key: "amount", header: "Amount", align: "right", render: (i) => (
          <span className="font-medium">{formatMoney(i.grand_total || i.amount)}</span>
        )},
        { key: "due", header: "Due", render: (i) => (
          i.due_date ? (
            <span className={i.is_overdue ? "text-red-600 font-bold text-xs" : "text-xs text-[#475569] dark:text-slate-400"}>
              {i.is_overdue && <AlertTriangle className="w-3 h-3 inline mr-0.5" />}
              {formatDate(i.due_date)}
            </span>
          ) : <span className="text-[#94A3B8] text-xs">—</span>
        )},
        { key: "status", header: "Status", render: (i) => <InvoiceStatusPill inv={i} /> },
        { key: "actions", header: "", align: "right", render: (i) => (
          <RowActions
            primary={{ label: "Open", icon: ExternalLink, onClick: () => navigate(`/admin/invoices/${i.invoice_id}`), testId: `open-inv-${i.invoice_id}` }}
          />
        )},
      ]}
      rows={invoices}
      getRowId={(i) => i.invoice_id}
      empty={<EmptyState icon={FileText} title="No invoices yet" body="Draft a new invoice from the header actions and it will land here." />}
    />
  );
}

function PaymentsTab({ payments }) {
  return (
    <DataTable
      testId="table-payments"
      columns={[
        { key: "date",   header: "Date", render: (p) => <span className="text-xs text-[#475569] dark:text-slate-400">{formatDate(p.created_at)}</span> },
        { key: "method", header: "Method", render: (p) => <span className="capitalize text-xs">{p.metadata?.method || "stripe"}</span> },
        { key: "amount", header: "Amount", align: "right", render: (p) => (
          <span className="text-emerald-600 font-medium inline-flex items-center gap-1 justify-end">
            <CheckCircle2 className="w-3.5 h-3.5" /> {formatMoney(p.amount)}
          </span>
        )},
        { key: "tx", header: "Reference", render: (p) => <span className="font-mono text-[10px] text-[#94A3B8]">{p.transaction_id?.slice(0, 14)}…</span> },
      ]}
      rows={payments}
      getRowId={(p) => p.transaction_id}
      empty={<EmptyState icon={Wallet} title="No payments yet" body="Successful payments from this client will appear here once they clear." />}
    />
  );
}

function RequestsTab({ requests }) {
  if (requests.length === 0) {
    return <EmptyState icon={MessageSquare} title="No service requests" body="Requests submitted from the client dashboard show up here in real time." />;
  }
  return (
    <ul className="space-y-2">
      {requests.map((r) => (
        <li key={r.request_id} className="border border-[#E2E8F0] dark:border-slate-700 rounded-lg p-3 hover:border-[#C9A356] transition-colors" data-testid={`row-req-${r.request_id}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-medium text-[#0A2540] dark:text-white capitalize truncate">
              {(r.request_type || "—").replace(/_/g, " ")}
            </div>
            <StatusPill tone={requestTone(r.status)} size="xs">{r.status}</StatusPill>
          </div>
          {r.notes && <p className="text-xs text-[#475569] dark:text-slate-400 mt-1.5 line-clamp-2">{r.notes}</p>}
          <div className="text-[10px] text-[#94A3B8] mt-1">{formatRelative(r.created_at)}</div>
        </li>
      ))}
    </ul>
  );
}

/* ==================== Sidebar cards ==================== */

function KeyDetailsCard({ user, stats }) {
  const items = [
    { label: "Customer #",   value: user.customer_number || user.customer_code || "—", mono: true },
    { label: "Joined",       value: formatDate(user.created_at) },
    { label: "Approved",     value: user.approved_at ? formatDate(user.approved_at) : "—" },
    { label: "Email",        value: user.email },
    { label: "Phone",        value: user.phone || "—" },
    { label: "Address",      value: user.address || "—" },
    { label: "Access level", value: user.access_level || "pending" },
    { label: "Total spend",  value: formatMoney(stats.paid_total || 0, { decimals: 0 }) },
  ];
  return (
    <div className="card-pb p-5" data-testid="sidebar-key-details">
      <h3 className="font-heading text-sm text-[#0A2540] dark:text-white inline-flex items-center gap-2 mb-3">
        <UserIcon className="w-4 h-4 text-[#C9A356]" /> Key details
      </h3>
      <dl className="text-sm space-y-2.5">
        {items.map((it) => (
          <div key={it.label} className="flex items-baseline justify-between gap-3">
            <dt className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold shrink-0">{it.label}</dt>
            <dd className={`text-right text-[#0A2540] dark:text-white truncate min-w-0 ${it.mono ? "font-mono text-xs" : "text-xs"}`}>
              {it.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function InternalNotesCard({ notes, setNotes, saving, onSave }) {
  return (
    <div className="card-pb border-l-4 border-l-amber-500 p-5" data-testid="sidebar-notes">
      <div className="flex items-center gap-2 mb-2">
        <Lock className="w-3.5 h-3.5 text-amber-600" />
        <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-400">
          Internal notes — admins only
        </span>
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={6}
        maxLength={5000}
        data-testid="notes-input"
        placeholder="Gate codes, follow-ups, discounts agreed, key contacts on-site…"
        className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm bg-slate-50/60 focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400 outline-none"
      />
      <div className="flex items-center justify-between mt-2">
        <span className="text-[10px] text-[#94A3B8] tabular-nums">{notes.length}/5000</span>
        <button onClick={onSave} disabled={saving}
                data-testid="save-notes"
                className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-[#0A2540] hover:bg-[#12365b] text-white text-xs font-semibold disabled:opacity-50">
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

/* ==================== Helpers ==================== */

function monthsSince(iso) {
  if (!iso) return 0;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return 0;
  const diff = (Date.now() - t.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
  return Math.max(0, Math.round(diff));
}

function buildActivityFeed({ projects, invoices, payments, requests, work_orders }) {
  const events = [];
  for (const p of projects) {
    events.push({
      id: p.project_id, type: "project",
      title: `Project “${p.title}” — ${(p.status || "").replace(/_/g, " ")}`,
      detail: p.project_number ? `${p.project_number} · ${(p.project_type || "").replace(/_/g, " ")}` : (p.project_type || "").replace(/_/g, " "),
      at: p.updated_at || p.created_at,
      link: `/admin/projects/${p.project_id}`,
      dot: p.status === "completed" ? "bg-emerald-500" : p.status === "cancelled" ? "bg-red-500" : "bg-sky-500",
    });
  }
  for (const i of invoices) {
    events.push({
      id: i.invoice_id, type: "invoice",
      title: `Invoice ${i.invoice_number || i.invoice_id} · ${(i.status || "").replace(/_/g, " ")}`,
      detail: `${formatMoney(i.grand_total || i.amount)}${i.due_date ? ` · due ${formatDate(i.due_date)}` : ""}`,
      at: i.sent_at || i.created_at,
      link: `/admin/invoices/${i.invoice_id}`,
      dot: i.status === "paid" ? "bg-emerald-500" : i.is_overdue ? "bg-red-500" : "bg-amber-500",
    });
  }
  for (const p of payments) {
    events.push({
      id: p.transaction_id, type: "payment",
      title: `Payment received · ${formatMoney(p.amount)}`,
      detail: `${p.metadata?.method || "stripe"}${p.metadata?.invoice_id ? ` · ${p.metadata.invoice_id}` : ""}`,
      at: p.created_at,
      dot: "bg-emerald-500",
    });
  }
  for (const r of requests) {
    events.push({
      id: r.request_id, type: "request",
      title: `Service request · ${(r.request_type || "").replace(/_/g, " ")}`,
      detail: r.notes,
      at: r.created_at,
      dot: r.status === "closed" ? "bg-slate-400" : "bg-[#C9A356]",
    });
  }
  for (const w of work_orders) {
    events.push({
      id: w.work_order_id, type: "wo",
      title: `Work order · ${(w.status || "").replace(/_/g, " ")}`,
      detail: w.title || w.description,
      at: w.updated_at || w.created_at,
      dot: w.status === "completed" ? "bg-emerald-500" : "bg-sky-500",
    });
  }
  return events
    .filter((e) => e.at)
    .sort((a, b) => new Date(b.at) - new Date(a.at));
}

function ProjectTypeIcon({ t }) {
  if (t === "solar_install" || t === "solar_upgrade") return <Sun className="w-3.5 h-3.5 text-[#C9A356]" />;
  if (t === "maintenance") return <Wrench className="w-3.5 h-3.5 text-[#C9A356]" />;
  return <Package className="w-3.5 h-3.5 text-[#C9A356]" />;
}

function ProjectPill({ status }) {
  const tone = status === "completed" ? "success"
             : status === "cancelled" ? "danger"
             : status === "installed"  ? "success"
             : status === "ordered"    ? "neutral"
             : "info";
  return <StatusPill tone={tone} dot>{(status || "").replace(/_/g, " ")}</StatusPill>;
}

function InvoiceStatusPill({ inv }) {
  if (inv.status === "paid") return <StatusPill tone="success" dot>Paid</StatusPill>;
  if (inv.is_overdue) return <StatusPill tone="danger" dot>Overdue</StatusPill>;
  if (inv.status === "draft") return <StatusPill tone="neutral">Draft</StatusPill>;
  if (inv.status === "sent" || inv.status === "open") return <StatusPill tone="warning" dot>Awaiting</StatusPill>;
  if (inv.status === "approved") return <StatusPill tone="info">Approved</StatusPill>;
  return <StatusPill tone="neutral">{inv.status}</StatusPill>;
}

function workOrderTone(s) {
  if (s === "completed") return "success";
  if (s === "cancelled") return "danger";
  if (s === "in_progress" || s === "scheduled") return "info";
  return "warning";
}
function requestTone(s) {
  if (s === "closed" || s === "resolved") return "success";
  if (s === "in_review") return "info";
  return "warning";
}
